import { useState } from 'react';
import { Link } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { forgotPasswordSchema, type ForgotPasswordData } from '@shared/schema';

export default function ForgotPasswordPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const { forgotPassword } = useAuth();

  const form = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordData) => {
    try {
      await forgotPassword(data.email);
      setIsSubmitted(true);
      toast({
        title: 'Reset email sent',
        description: 'Check your email for password reset instructions.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to send reset email',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-apple-blue via-apple-purple to-apple-pink relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="floating-orb bg-gradient-to-r from-apple-blue/20 to-apple-purple/20 w-96 h-96 rounded-full blur-3xl -top-48 -left-48 animate-float-slow" />
        <div className="floating-orb bg-gradient-to-r from-apple-purple/20 to-apple-pink/20 w-80 h-80 rounded-full blur-3xl top-1/3 -right-40 animate-float-reverse" />
        <div className="floating-orb bg-gradient-to-r from-apple-pink/20 to-apple-orange/20 w-64 h-64 rounded-full blur-3xl bottom-1/4 left-1/4 animate-float-slow" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          {/* Back button */}
          <Link href="/login">
            <Button 
              variant="ghost" 
              className="mb-6 text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Button>
          </Link>

          <Card className="glass-effect border-white/20 shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-white to-apple-gray bg-clip-text text-transparent">
                Reset Password
              </CardTitle>
              <CardDescription className="text-white/70">
                {isSubmitted 
                  ? "We've sent you a password reset link"
                  : "Enter your email to receive a password reset link"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isSubmitted ? (
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-r from-apple-blue to-apple-purple rounded-full flex items-center justify-center">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-white/80">
                    Check your email inbox and click the link to reset your password.
                  </p>
                  <p className="text-sm text-white/60">
                    Didn't receive the email? Check your spam folder or{' '}
                    <Button
                      variant="link"
                      className="text-white hover:text-apple-blue p-0 h-auto"
                      onClick={() => setIsSubmitted(false)}
                    >
                      try again
                    </Button>
                  </p>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white/90">Email Address</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="your@email.com"
                              type="email"
                              {...field}
                              className="glass-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full apple-button"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </form>
                </Form>
              )}

              <div className="text-center">
                <span className="text-white/60">Remember your password? </span>
                <Link href="/login">
                  <Button
                    variant="link"
                    className="text-white hover:text-apple-blue p-0 h-auto"
                  >
                    Sign in
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}